import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { STRINGS } from '@/strings/strings';
import { useAuthStore } from '@/stores/authStore';
import { ApiError } from '@/lib/api-client';
import { fireGa4BookFromFigurenbild, fireGa4FigurenbildGenerated } from '@/lib/analytics/gtag';
import { usePhotoUpload } from '@/features/book/use-photo-upload';
import { useBookConfiguratorStore } from '@/features/book/state/book-configurator-store';
import { putPhotoPick } from '@/features/book/state/photo-picks-idb';
import { ProbeCard } from '../components/ProbeCard';
import { ProbeLightGateOverlay } from '../components/ProbeLightGateOverlay';
import { PROBE_FORM_MAX_CARDS, useProbeStore } from '../probe-store';
import {
  createProbe,
  fetchProbeStatus,
  fetchProbeTransfer,
  type CreateProbePersonPayload,
  type ProbeStatusResponse,
} from '../api';

/**
 * Probecover Stage 2 P5 — /figuren (form first, account second).
 *
 * The route is PUBLIC. Logged-out visitors see the form, fill in
 * their figures + photos, click Figurenbild erstellen. Submit then
 * either:
 *   - Logged-OUT: opens `ProbeLightGateOverlay` for register
 *     (email + password + Turnstile + terms + pre-checked
 *     Bestandskunden-Hinweis per § 7 Abs. 3 UWG,
 *     signupSource='probecover'). On success the overlay
 *     fires onAuthenticated → FigurenRoute runs its existing probe-
 *     create flow (upload + createProbe). The returning-customer
 *     "Du hast schon ein Konto?" link inside the overlay offers a
 *     login path.
 *   - Logged-IN: runs probe-create immediately. No marketing
 *     surface on this route — opt-out happens via the unsubscribe
 *     link in each promo mail or /api/mail/unsubscribe directly.
 *
 * Deep link `?probe=<id>` keeps working: a mail recipient opening
 * the link while logged out will see the empty form (we do NOT
 * open the overlay automatically — a deep-link to a result they
 * own needs a login path, which the overlay's "schon ein Konto?"
 * toggle provides).
 *
 * Backend cap enforcement: `ProbeService.createProbe` enforces the
 * 2-per-account cap AFTER authentication. The first probe for a
 * newly-registered customer is free; a third fails cleanly with
 * `probe_cap_reached` (envelope rendered by the error banner).
 *
 * Feature-flag gate: `user.featureFlags.probecover`. Only applied
 * when the customer is LOGGED IN — a logged-out visitor can still
 * see the form (mail link → page → overlay → signup pipeline must
 * work for a flag-off account too, otherwise the mail is a dead
 * end).
 */
export function FigurenRoute() {
  const user = useAuthStore((s) => s.user);
  const fetchCurrentUser = useAuthStore((s) => s.fetchCurrentUser);
  const { cards, addCard, removeCard, updateCard, setPhoto, setPhotoKey, reset } = useProbeStore();
  const photoUpload = usePhotoUpload();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const resetConfigurator = useBookConfiguratorStore((s) => s.reset);
  const addConfiguratorCast = useBookConfiguratorStore((s) => s.addCast);
  const updateConfiguratorCast = useBookConfiguratorStore((s) => s.updateCast);
  const [transferring, setTransferring] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Stage 2 P5 — overlay state. Shown when a logged-out visitor
  // tries to submit; the overlay signals success via
  // `handleAuthenticated`, which fires the existing probe-create
  // flow once the auth store has flipped user null → set.
  // F12 — also opens automatically when the deep-link lands
  // while logged out (see effect below).
  const [lightGateOpen, setLightGateOpen] = useState(false);
  // F12 (2026-10-17) — pre-fill overlay in LOGIN mode for a
  // deep-link opened logged out (returning customer path).
  // Register mode stays the default for the form-submit path.
  // F15 state (declared early so the F11 companion effect below
  // can gate on `userChoseAnother`). See handleAnother for the
  // write side.
  const [userChoseAnother, setUserChoseAnother] = useState(false);
  const [showCapAtRest, setShowCapAtRest] = useState(false);
  const [lightGateMode, setLightGateMode] = useState<'register' | 'login'>('register');
  // Stage 2 P2 — honor ?probe=<id> deep link from the probe_ready
  // mail. Seed `probeId` from the query param on mount so the polling
  // effect + the result/failure render branches pick up the existing
  // probe row without the customer going through the form again. The
  // backend route is owner-only (JwtAuthGuard + userId check in
  // ProbeService.getStatus).
  //
  // F12 (Sebastian field finding 2026-10-17) — logged-OUT visitors
  // opening a deep link must see the LightGate in LOGIN mode
  // immediately. Previously they saw an infinite "Wir zeichnen
  // euch gerade" waiting screen because the probe-id seed fired
  // polling against the owner-only endpoint without auth; the 401
  // was caught but the UI stayed in the waiting branch. Now we
  // DELAY seeding `probeId` until the user is authenticated —
  // the overlay-open effect below also opens the overlay
  // automatically when `?probe=<id>` is present and `user === null`.
  const probeFromQuery = searchParams.get('probe');
  // F11 (Sebastian field finding 2026-10-17) — if the user is
  // logged in AND has no `?probe=` query AND has a latest probe
  // (probe_rendering or probe_ready, server-filtered), seed
  // `probeId` from the latest probe so /figuren shows the
  // existing waiting / result screen instead of a fresh form.
  // The customer's "Noch ein Figurenbild" on the result still
  // leads to the form via `handleAnother` (clears probeId +
  // the query param); the 2-per-account cap stays
  // backend-enforced.
  const latestProbeId = user !== null ? (user.latestProbe?.id ?? null) : null;
  const [probeId, setProbeId] = useState<string | null>(() => {
    if (probeFromQuery !== null && user !== null) return probeFromQuery;
    if (probeFromQuery === null && latestProbeId !== null) return latestProbeId;
    return null;
  });
  const [polledStatus, setPolledStatus] = useState<ProbeStatusResponse | null>(null);

  const probecoverEnabled = user?.featureFlags?.probecover ?? false;

  // F12 (Sebastian field finding 2026-10-17) — deep-link login
  // bootstrap. When the URL carries `?probe=<id>` AND the
  // visitor is logged OUT, open the LightGate overlay in LOGIN
  // mode immediately so the returning customer sees "Willkommen
  // zurück" (not a never-ending waiting screen).
  //
  // Guards:
  //   - Only runs while `user === null`. Logged-in users with a
  //     deep link skip this effect entirely.
  //   - Only ONCE per mount per deep link. If the customer
  //     dismisses the overlay (closes the dialog), the lightGate
  //     open state goes to false; we do NOT re-open on the same
  //     mount — the dismissal is the explicit "I'd rather see
  //     the form" action.
  //
  // After `onAuthenticated` fires (login succeeds), the user
  // flip to non-null drives a separate effect below that seeds
  // `probeId` from the query param, which then starts polling
  // the (now owner-matched) probe for its result.
  const probeDeepLinkOpenedRef = useRef(false);
  useEffect(() => {
    if (probeDeepLinkOpenedRef.current) return;
    if (user !== null) return;
    if (probeFromQuery === null) return;
    probeDeepLinkOpenedRef.current = true;
    setLightGateMode('login');
    setLightGateOpen(true);
  }, [user, probeFromQuery]);

  // F12 companion — after a successful login via the deep-link
  // overlay, the user flips null → set. At that moment the
  // local `probeId` is still null (we deferred seeding on
  // mount). Seed it now so the polling effect fires.
  //
  // F11 extension — also seeds from `user.latestProbe.id` when
  // the login path happens WITHOUT a deep-link (plain
  // /figuren, logged-in via header). The initial-state seed
  // above only fires at mount; a late user hydration needs
  // this effect to catch up.
  //
  // F15 extension — skip the auto-seed when the user has
  // deliberately clicked "Noch ein Figurenbild". Pre-fix the
  // seed re-filled probeId from `user.latestProbe.id` on the
  // same tick that `handleAnother` cleared it, making the
  // click a visible no-op. The `userChoseAnother` flag is
  // reset only on unmount; a tab visit after the user left
  // the page re-hydrates the result (plain F11 path).
  useEffect(() => {
    if (user === null) return;
    if (probeId !== null) return;
    if (userChoseAnother) return;
    if (probeFromQuery !== null) {
      setProbeId(probeFromQuery);
      return;
    }
    if (user.latestProbe?.id) {
      setProbeId(user.latestProbe.id);
    }
  }, [user, probeFromQuery, probeId, userChoseAnother]);

  // Pre-2026-10-31 non-pre-checked consent checkbox + POST
  // /users/consent write removed in favour of the pre-checked
  // Bestandskunden-Hinweis (§ 7 Abs. 3 UWG) on the three register
  // screens. Logged-in users who want to opt out use the unsubscribe
  // link in each promo mail OR /api/mail/unsubscribe directly.

  // ─── Poll /api/figuren/orders/:id ──────────────────────────────
  //
  // F15 extension (Sebastian field finding 2026-10-17) — when the
  // polled status transitions to `probe_ready`, refetch `/me` so
  // `user.probeCapReached` reflects the current server state. The
  // mid-session cap-reach case (customer below cap on page load,
  // 2nd probe finishes rendering) was pre-fix invisible: polling
  // would hand back the result + "Noch ein Figurenbild" would
  // read the stale `probeCapReached: false` from the authStore
  // user hydrated at login time. The /me refetch is best-effort
  // (non-fatal if it fails — a stale cap flag still fails cleanly
  // at the backend `probe_cap_reached` gate on the next submit).
  useEffect(() => {
    if (probeId === null) return;
    if (polledStatus?.status === 'probe_ready') return;
    if (polledStatus?.status === 'probe_render_failed') return;
    let cancelled = false;
    const tick = async (): Promise<void> => {
      try {
        const resp = await fetchProbeStatus(probeId);
        if (cancelled) return;
        setPolledStatus(resp);
        if (resp.status === 'probe_rendering') {
          setTimeout(() => void tick(), 2000);
        } else if (resp.status === 'probe_ready') {
          // F15 — probe finished rendering → cap state may have
          // flipped. Refresh `/me` so the "Noch ein Figurenbild"
          // click reads the fresh `probeCapReached`.
          try {
            await fetchCurrentUser();
          } catch {
            // Non-fatal — backend `probe_cap_reached` guard is
            // the ground truth on the next submit.
          }
        }
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(err instanceof Error ? err.message : String(err));
      }
    };
    void tick();
    return () => {
      cancelled = true;
    };
  }, [probeId, polledStatus?.status, fetchCurrentUser]);

  // GA4 funnel — figurenbild_generated (Sebastian ruling 2026-10-21).
  // Fires exactly once when the status flips to `probe_ready` AND a
  // watermarked image URL is present (same condition the result UI
  // uses to render). Guarded by a per-probeId ref sentinel so a
  // re-render on the same ready probe does not re-fire. Figurenbild
  // is behind FEATURE_PROBECOVER_ENABLED (OFF in prod); this effect
  // is dormant until that flag flips on.
  const fired4GeneratedForProbeIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (probeId === null) return;
    if (polledStatus?.status !== 'probe_ready') return;
    if (polledStatus.watermarkedUrl === null) return;
    if (fired4GeneratedForProbeIdRef.current === probeId) return;
    fired4GeneratedForProbeIdRef.current = probeId;
    fireGa4FigurenbildGenerated({ probe_id: probeId });
  }, [probeId, polledStatus?.status, polledStatus?.watermarkedUrl]);

  // F15 extension — refresh `/me` on mount for a logged-in user.
  // The authStore persists `user` to localStorage; a page reload
  // rehydrates the user object from the last `/me` snapshot.
  // Without this refetch, a reload lands with the OLD
  // `probeCapReached` state and the "Noch ein Figurenbild" click
  // branches on stale data. Best-effort — a transient failure
  // leaves the persisted user in place (the backend's own
  // `probe_cap_reached` gate still fires on an actual submit).
  const didMountRefreshRef = useRef(false);
  useEffect(() => {
    if (didMountRefreshRef.current) return;
    if (user === null) return;
    didMountRefreshRef.current = true;
    void (async () => {
      try {
        await fetchCurrentUser();
      } catch {
        // non-fatal
      }
    })();
  }, [user, fetchCurrentUser]);

  // Validity rule (Sebastian ruling 2026-10-17 — supersedes the
  // booth's silent-'keineAngabe' default for tier). Both person
  // and tier require photo + age + a gender choice; tier
  // additionally requires species (free-text or Hund/Katze
  // shortcut, trimmed > 0). The gender choice is now the
  // customer's, not a hard-coded default — the booth-side
  // "pinned to 'keineAngabe' at submit" convention has been
  // dropped.
  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (cards.length === 0) return false;
    return cards.every((c) => {
      if (c.photoFile === null) return false;
      if (c.ageYears === null) return false;
      if (c.geschlecht === '') return false;
      if (c.personType === 'tier') {
        return c.species.trim().length > 0;
      }
      return true;
    });
  }, [cards, submitting]);

  const runProbeCreate = useCallback(async () => {
    setSubmitting(true);
    setErrorMsg(null);
    try {
      // 3a — upload every card's photo.
      const files = cards.map((c) => c.photoFile!).filter((f) => f !== null);
      const keys = await photoUpload.upload(files as readonly File[]);
      cards.forEach((c, i) => setPhotoKey(c.id, keys[i] ?? null));
      // 3c — POST /api/figuren/orders. Sebastian ruling 2026-10-17:
      // tier gender is now a customer choice, same as person. The
      // pre-ruling booth override that pinned tier geschlecht to
      // 'keineAngabe' server-side-irrespective has been dropped;
      // both branches pass `c.geschlecht` through as the customer
      // chose. The form's `canSubmit` guard above already asserts
      // a non-empty geschlecht for both branches.
      const payload: readonly CreateProbePersonPayload[] = cards.map((c, i) => {
        const geschlecht = c.geschlecht as 'maennlich' | 'weiblich' | 'keineAngabe';
        if (c.personType === 'tier') {
          return {
            personType: 'tier' as const,
            ageYears: c.ageYears!,
            geschlecht,
            species: c.species.trim(),
            photoKey: keys[i]!,
          };
        }
        return {
          personType: 'person' as const,
          ageYears: c.ageYears!,
          geschlecht,
          photoKey: keys[i]!,
        };
      });
      const { probeId: newProbeId } = await createProbe({ persons: payload });
      setProbeId(newProbeId);
    } catch (err) {
      if (err instanceof ApiError && typeof err.envelope?.user_message === 'string') {
        setErrorMsg(err.envelope.user_message);
      } else {
        setErrorMsg(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setSubmitting(false);
    }
  }, [cards, photoUpload, setPhotoKey]);

  // Stage 2 P5 — form submit. Logged-OUT visitors see the
  // ProbeLightGate overlay first; logged-IN customers run the
  // probe-create pipeline immediately. The overlay's
  // onAuthenticated callback below closes the overlay and chains
  // into runProbeCreate once the auth store has the new session.
  const handleSubmit = useCallback(() => {
    if (user === null) {
      setErrorMsg(null);
      setLightGateOpen(true);
      return;
    }
    void runProbeCreate();
  }, [user, runProbeCreate]);

  const handleAuthenticated = useCallback(() => {
    setLightGateOpen(false);
    // F12 — if the overlay opened for the deep-link path (no
    // form submit in progress, just a returning customer
    // viewing their result), DO NOT run probe-create. The
    // "seed probeId after login" effect above picks up the
    // query param → polling fires → result screen renders.
    // Form-submit path: form has cards + `canSubmit` was true
    // when the overlay was opened via handleSubmit; the probe
    // store's cards survive the overlay lifecycle.
    if (probeFromQuery !== null) {
      return;
    }
    void runProbeCreate();
  }, [runProbeCreate, probeFromQuery]);

  // Stage 2 P4 — "Daraus ein Buch machen" — pre-fill the book
  // configurator from this probe and navigate into /book.
  //
  // Overwrite behaviour (Sebastian ruling 2026-10-15 follow-up):
  // NO dialog. `resetConfigurator()` wipes ONLY the local Zustand
  // draft (persisted under `dreamable-book-configurator` in the
  // browser) before seeding the probe figures. Server-side
  // BookOrders are NOT touched — the customer's existing "Meine
  // Bücher" list stays complete. The invariant is locked by
  // `FigurenRoute.transfer.test.tsx` which asserts no network call
  // against `/api/book/orders` fires during this handler.
  const handleToBook = useCallback(async () => {
    if (probeId === null || transferring) return;
    // GA4 funnel — book_from_figurenbild (Sebastian ruling 2026-10-21).
    // Fires BEFORE the transfer so the funnel captures customer intent
    // even if the downstream fetch fails. Fires at most once per click
    // (guarded by the `transferring` flag + button-disabled state).
    fireGa4BookFromFigurenbild({ probe_id: probeId });
    setTransferring(true);
    setErrorMsg(null);
    try {
      const transfer = await fetchProbeTransfer(probeId);

      // Fetch every photo byte-blob BEFORE touching the store. If a
      // fetch fails we abort cleanly — the configurator stays
      // untouched + the customer sees an error.
      const files = await Promise.all(
        transfer.figures.map(async (fig) => {
          const resp = await fetch(fig.photoDownloadUrl);
          if (!resp.ok) {
            throw new Error(`photo fetch failed (${resp.status}) for figure ${fig.characterKey}`);
          }
          const bytes = await resp.arrayBuffer();
          return new File([bytes], fig.photoFilename, { type: fig.photoContentType });
        }),
      );

      // Clean slate — see JSDoc overwrite behaviour note above.
      resetConfigurator();

      // Seed each figure. `addConfiguratorCast()` returns the fresh
      // castKey; `updateConfiguratorCast` writes personType / age /
      // gender / species; `putPhotoPick` writes the File into IDB
      // under the SAME castKey so PersonenRoute's hydration pass
      // finds the photo on first render.
      //
      // Note: INITIAL_STATE seeds one empty cast entry. The first
      // probe figure REUSES that seed entry (first `addCast` emits
      // its castKey? No — addCast appends a new entry. The seed
      // entry stays and ends up as a trailing blank). To avoid a
      // trailing blank we read the seed entry's key off the store
      // AFTER reset, update it in place for figure 0, and addCast
      // for figures 1..N.
      const stateAfterReset = useBookConfiguratorStore.getState();
      for (let i = 0; i < transfer.figures.length; i += 1) {
        const fig = transfer.figures[i]!;
        const file = files[i]!;
        let castKey: string;
        if (i === 0 && stateAfterReset.cast.length > 0) {
          castKey = stateAfterReset.cast[0]!.key;
        } else {
          castKey = addConfiguratorCast();
        }
        updateConfiguratorCast(castKey, {
          personType: fig.personType === 'tier' ? 'tier' : 'person',
          ageYears: fig.ageYears,
          geschlecht: fig.geschlecht,
          species: fig.species,
          // name intentionally left empty — Sebastian ruling:
          // customer picks book-side names, not the synthetic
          // "Person N" / "Tier N" labels.
        });
        await putPhotoPick(castKey, 'face', file);
      }

      // Navigate into /book, escaping the audio-onboarding gate
      // (`/figuren` is already in ONBOARDING_BYPASS_PATHS; /book is
      // the configurator entry). The `fromNewBookIntent: true`
      // location-state marker is the same bypass Meine-Bücher →
      // "Neues Buch anlegen" uses: PersonenRoute's Case-1 mount
      // redirect at `PersonenRoute.tsx:210-222` short-circuits when
      // this flag is set, so the probe seed survives even when the
      // customer has an open pre-payment draft. Without the flag,
      // `localDraftHasContent` returns false (probe seeds leave
      // `name` empty) and W28 would route to the pending draft,
      // destroying the transfer. The reload-after-transfer edge
      // case is covered by extending `localDraftHasContent` to
      // count a cast entry with `ageYears !== null` as content
      // (sister commit on `book-configurator-store.ts`).
      // Replace so the back button doesn't trap the customer on
      // the probe result screen.
      navigate('/book', { replace: true, state: { fromNewBookIntent: true } });
    } catch (err) {
      if (err instanceof ApiError && typeof err.envelope?.user_message === 'string') {
        setErrorMsg(err.envelope.user_message);
      } else {
        setErrorMsg(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setTransferring(false);
    }
  }, [
    addConfiguratorCast,
    navigate,
    probeId,
    resetConfigurator,
    transferring,
    updateConfiguratorCast,
  ]);

  // F15 (Sebastian field finding 2026-10-17) — "Noch ein
  // Figurenbild" has two meanings depending on cap state:
  //   - below cap → clear probeId + show the form (unchanged).
  //   - at cap    → show the backend cap message inline on the
  //                 result screen (NOT a silent no-op, NOT a
  //                 re-seed back to the result).
  // State is declared earlier so the F11 companion effect can
  // gate on `userChoseAnother`; this handler is the write side.
  const handleAnother = useCallback(() => {
    if (user?.probeCapReached === true) {
      // Cap gate — show the backend-mirrored message in place.
      // Buttons stay enabled so the user can still click
      // "Daraus ein Buch machen" (which navigates to /book and
      // starts the book flow — cap does not apply there).
      setShowCapAtRest(true);
      return;
    }
    setShowCapAtRest(false);
    setUserChoseAnother(true);
    setProbeId(null);
    setPolledStatus(null);
    setErrorMsg(null);
    reset();
    // Stage 2 P2 — clear ?probe=<id> from the URL so a deep-linked
    // result view transitions cleanly back to the form (otherwise the
    // next render would re-seed probeId from the stale query param).
    if (searchParams.has('probe')) {
      const next = new URLSearchParams(searchParams);
      next.delete('probe');
      setSearchParams(next, { replace: true });
    }
  }, [reset, searchParams, setSearchParams, user]);

  // ─── Render branches ──────────────────────────────────────────
  // Stage 2 P5 — /figuren is public (form first, account second).
  // The pre-existing `return <Navigate to="/auth/login...">` for
  // user===null has been removed. A logged-OUT visitor sees the
  // form; submit triggers the overlay. The feature-flag gate still
  // fires for LOGGED-IN users whose flag happens to be off (defense-
  // in-depth: a probecover=false logged-in user cannot bypass the
  // flag by navigating here).
  if (user !== null && !probecoverEnabled) {
    return <Navigate to="/" replace />;
  }

  if (polledStatus?.status === 'probe_ready' && polledStatus.watermarkedUrl !== null) {
    return (
      <main className="mx-auto flex max-w-xl flex-col gap-6 px-4 py-10">
        <h1 className="text-2xl font-bold text-dream-navy">{STRINGS.book.probe.result.heading}</h1>
        <img
          src={polledStatus.watermarkedUrl}
          alt="Dein Probe-Figurenbild"
          className="w-full rounded-lg border border-dream-navy/15"
        />
        <p className="text-sm text-dream-navy/70">{STRINGS.book.probe.result.watermarkCaption}</p>
        {/* Stage 2 P4 — primary CTA: pre-fills the book configurator
            from this probe + navigates to /book. */}
        <button
          type="button"
          onClick={() => void handleToBook()}
          disabled={transferring}
          className="h-12 rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {transferring ? (
            <span className="inline-flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {STRINGS.book.probe.result.toBookCta}
            </span>
          ) : (
            STRINGS.book.probe.result.toBookCta
          )}
        </button>
        <button
          type="button"
          onClick={handleAnother}
          disabled={transferring}
          className="h-12 rounded-lg border-[1.5px] border-dream-navy bg-dream-white px-8 text-base font-semibold text-dream-navy hover:bg-dream-navy hover:text-dream-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {STRINGS.book.probe.result.anotherCta}
        </button>
        {/* F15 — at-cap message surfaced in the same error-styled
            red text used for the form-submit cap banner. Mirrors
            the backend `probe_cap_reached` catalog entry
            byte-for-byte via `capReachedInline`. */}
        {showCapAtRest && (
          <p role="alert" className="text-sm text-error">
            {STRINGS.book.probe.result.capReachedInline}
          </p>
        )}
        {errorMsg !== null && (
          <p role="alert" className="text-sm text-error">
            {errorMsg}
          </p>
        )}
      </main>
    );
  }

  if (polledStatus?.status === 'probe_render_failed') {
    return (
      <main className="mx-auto flex max-w-xl flex-col gap-6 px-4 py-10">
        <h1 className="text-2xl font-bold text-dream-navy">{STRINGS.book.probe.failure.heading}</h1>
        <p className="text-base text-dream-navy/80">{STRINGS.book.probe.failure.body}</p>
        <button
          type="button"
          onClick={handleAnother}
          className="h-12 rounded-lg border-[1.5px] border-dream-navy bg-dream-white px-8 text-base font-semibold text-dream-navy"
        >
          {STRINGS.book.probe.result.anotherCta}
        </button>
      </main>
    );
  }

  if (probeId !== null) {
    return (
      <main className="mx-auto flex min-h-[24rem] max-w-xl flex-col items-center justify-center gap-4 px-4 py-10 text-center">
        <h1 className="text-xl font-semibold text-dream-navy">
          {STRINGS.book.probe.waiting.heading}
        </h1>
        <p className="text-base text-dream-navy/80">{STRINGS.book.probe.waiting.body}</p>
        <Loader2 className="h-8 w-8 animate-spin text-dream-navy/70" aria-hidden />
      </main>
    );
  }

  // Booth-shaped single-panel layout (feat/booth-local:src/booth/public/
  // booth.html — ".layout > .panel.panel--form"): white panel, drop
  // shadow, 20 px padding, title, cards column, add row with counter,
  // consent checkbox, full-width gold submit. The two reserved text
  // slots (section title, submit label) stay placeholders per the
  // build prompt.
  return (
    <main className="mx-auto max-w-xl px-4 py-6">
      <header className="mb-5">
        <h1 className="text-2xl font-bold text-dream-navy">{STRINGS.book.probe.routeHeading}</h1>
        <p className="mt-1 text-base text-dream-navy/80">{STRINGS.book.probe.routeSubheading}</p>
      </header>

      <section className="rounded-xl bg-dream-white p-5 shadow-[0_12px_32px_-12px_rgba(10,35,66,0.14)]">
        <h2 className="mb-4 text-[18px] font-semibold text-dream-navy">
          {STRINGS.book.probe.form.sectionTitle}
        </h2>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) void handleSubmit();
          }}
          className="flex flex-col gap-3"
          noValidate
        >
          <div className="flex flex-col gap-3">
            {cards.map((card, i) => (
              <ProbeCard
                key={card.id}
                card={card}
                index={i}
                canRemove={cards.length > 1}
                onUpdate={(patch) => updateCard(card.id, patch)}
                onRemove={() => removeCard(card.id)}
                onPickPhoto={(file) => setPhoto(card.id, file)}
              />
            ))}
          </div>

          {/* "+ Person" button + "N / 9" counter (booth .form__add row) */}
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={addCard}
              disabled={cards.length >= PROBE_FORM_MAX_CARDS}
              className="rounded-md border border-dream-navy bg-dream-white px-4 py-2.5 text-[14px] font-semibold text-dream-navy hover:bg-dream-navy hover:text-dream-white disabled:cursor-not-allowed disabled:opacity-45"
            >
              + Person
            </button>
            <span className="text-[13px] text-dream-navy/70 tabular-nums">
              {cards.length} / {PROBE_FORM_MAX_CARDS}
            </span>
          </div>

          {errorMsg !== null && (
            <p role="alert" className="text-sm text-error">
              {errorMsg}
            </p>
          )}

          {/* Full-width gold submit (booth .btn.btn--primary.btn--block).
              Reserved placeholder label — "Cover erstellen" is NOT
              reused per Sebastian's rule (the probe produces one
              family image, not a cover). */}
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full rounded-md bg-dream-gold px-4.5 py-3.5 text-[17px] font-semibold text-dream-navy shadow-[0_1px_2px_0_rgba(10,35,66,0.1)] hover:bg-[#ffd770] disabled:cursor-not-allowed disabled:opacity-45"
          >
            {submitting ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                {STRINGS.book.probe.form.submitCta}
              </span>
            ) : (
              STRINGS.book.probe.form.submitCta
            )}
          </button>
        </form>
      </section>

      {/* Stage 2 P5 — overlay rendered unconditionally; `open` is
          toggled by handleSubmit when the visitor is logged-out.
          F12 — also toggled by the deep-link bootstrap effect
          above when `?probe=<id>` is present on a logged-out
          visitor; in that path `initialMode='login'` so the
          overlay opens on "Willkommen zurück". */}
      <ProbeLightGateOverlay
        open={lightGateOpen}
        onOpenChange={setLightGateOpen}
        onAuthenticated={handleAuthenticated}
        initialMode={lightGateMode}
      />
    </main>
  );
}
